"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_RMS_FORMAT_STRING = void 0;
exports.getRMSInputString = getRMSInputString;
exports.evalTemplateExpression = evalTemplateExpression;
exports.interpolateFormatString = interpolateFormatString;
exports.resolveTemplateVariables = resolveTemplateVariables;
exports.selectRmsFormat = selectRmsFormat;
exports.getFormattedRmsCopy = getFormattedRmsCopy;
exports.formatReservationDetails = formatReservationDetails;
const TDR_1 = __importDefault(require("../types/TDR"));
const invoice_1 = require("./invoice");
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
function getRMSInputString(reservation, table, restaurant) {
    var _a, _b;
    const rmsFormatString = (_b = (_a = table === null || table === void 0 ? void 0 : table.rmsFormat) !== null && _a !== void 0 ? _a : restaurant === null || restaurant === void 0 ? void 0 : restaurant.rmsFormat) !== null && _b !== void 0 ? _b : TDR_1.default.RMSFormat.TWO_STARS;
    switch (rmsFormatString) {
        case TDR_1.default.RMSFormat.TWO_STARS:
            return `⭐⭐${reservation.firstName} ${reservation.lastName}`;
        case TDR_1.default.RMSFormat.NO_STARS:
            return `${reservation.firstName} ${reservation.lastName}`;
        case TDR_1.default.RMSFormat.TWO_DOTS:
            return `••${reservation.firstName} ${reservation.lastName}`;
        case TDR_1.default.RMSFormat.PATIO:
            return `⛱️☀️${reservation.firstName} ${reservation.lastName}`;
        case TDR_1.default.RMSFormat.STAR_TABLZ:
            return `⭐️TABLZ - ${reservation.firstName} ${reservation.lastName}`;
        case TDR_1.default.RMSFormat.END_STARS:
            return `${reservation.firstName} ${reservation.lastName} ⭐⭐`;
        case TDR_1.default.RMSFormat.CHAIR_STAR:
            return `🪑⭐${reservation.firstName} ${reservation.lastName}`;
        default:
            return `⭐⭐${reservation.firstName} ${reservation.lastName}`;
    }
}
exports.DEFAULT_RMS_FORMAT_STRING = `{{paidPrefix}}TABLZ Reservation - Please do not move: {{tableIdentifier}}

Occasion: {{occasion?}}

Items: {{addons?}}, payment due at restaurant.

Guest Notes: {{guestNotes?}}

{{depositPatternBlock?}}
{{depositBlock?}}
{{depositMessage?}}

{{tableFeeLine?}}`;
function formatCurrency(amount, currency) {
    const code = (currency !== null && currency !== void 0 ? currency : 'USD').toUpperCase();
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
    }
    catch (_a) {
        return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
    }
}
function isOptionalFalsy(value) {
    if (value === undefined || value === null) {
        return true;
    }
    return value.trim() === '';
}
function parseNumericFromVar(value) {
    if (value === undefined || value === null || value === '')
        return 0;
    const clean = String(value).replace(/[^0-9.\-]/g, '');
    const n = parseFloat(clean);
    return isNaN(n) ? 0 : n;
}
function evaluateArithmeticExpression(expr) {
    let pos = 0;
    const peek = () => expr[pos];
    const skipSpace = () => { while (pos < expr.length && /\s/.test(expr[pos]))
        pos++; };
    function parseNumber() {
        skipSpace();
        const start = pos;
        while (pos < expr.length && /[\d.]/.test(expr[pos]))
            pos++;
        if (pos === start)
            throw new Error('Expected number');
        const n = parseFloat(expr.slice(start, pos));
        if (isNaN(n))
            throw new Error('Invalid number');
        return n;
    }
    function parsePrimary() {
        skipSpace();
        if (peek() === '(') {
            pos++;
            const value = parseExpression();
            skipSpace();
            if (peek() !== ')')
                throw new Error('Expected )');
            pos++;
            return value;
        }
        return parseNumber();
    }
    function parseUnary() {
        skipSpace();
        if (peek() === '-') {
            pos++;
            return -parseUnary();
        }
        if (peek() === '+') {
            pos++;
            return parseUnary();
        }
        return parsePrimary();
    }
    function parseTerm() {
        let value = parseUnary();
        skipSpace();
        while (pos < expr.length && (peek() === '*' || peek() === '/' || peek() === '%')) {
            const op = peek();
            pos++;
            const rhs = parseUnary();
            if (op === '*')
                value *= rhs;
            else if (op === '/')
                value /= rhs;
            else
                value %= rhs;
            skipSpace();
        }
        return value;
    }
    function parseExpression() {
        let value = parseTerm();
        skipSpace();
        while (pos < expr.length && (peek() === '+' || peek() === '-')) {
            const op = peek();
            pos++;
            const rhs = parseTerm();
            value = op === '+' ? value + rhs : value - rhs;
            skipSpace();
        }
        return value;
    }
    const result = parseExpression();
    skipSpace();
    if (pos !== expr.length)
        throw new Error('Unexpected trailing input');
    return result;
}
function safeEvalArithmetic(expr) {
    if (!/^[\d\s+\-*/.()%]+$/.test(expr))
        return NaN;
    try {
        return evaluateArithmeticExpression(expr);
    }
    catch (_a) {
        return NaN;
    }
}
/**
 * Evaluates a template expression like `70 * guests` against a variables map.
 * Variable names are replaced with their numeric values before arithmetic evaluation.
 * Returns a formatted string: integer results as-is, decimals to 2 places.
 * Returns '' for invalid or non-finite results.
 *
 * Syntax in format strings:
 *   {{= 70 * guests}}   — substitute result
 *   {{= 70 * guests?}}  — substitute result; omit the whole line if result is 0
 */
function evalTemplateExpression(expr, variables) {
    const withVars = expr.replace(/\b([a-zA-Z_]\w*)\b/g, (_, name) => {
        return String(parseNumericFromVar(variables[name]));
    });
    const result = safeEvalArithmetic(withVars);
    if (!isFinite(result) || isNaN(result))
        return '';
    return Number.isInteger(result) ? String(result) : result.toFixed(2);
}
function interpolateFormatString(formatString, variables) {
    const lines = formatString.split('\n');
    const kept = [];
    for (const line of lines) {
        let skip = false;
        for (const m of line.matchAll(/\{\{(\w+)\?\}\}/g)) {
            if (isOptionalFalsy(variables[m[1]])) {
                skip = true;
                break;
            }
        }
        if (!skip) {
            for (const m of line.matchAll(/\{\{=\s*([\s\S]+?)\s*\}\}/g)) {
                const content = m[1];
                if (!content.endsWith('?'))
                    continue;
                const expr = content.slice(0, -1).trimEnd();
                const val = evalTemplateExpression(expr, variables);
                if (!val || val === '0') {
                    skip = true;
                    break;
                }
            }
        }
        if (skip)
            continue;
        kept.push(line);
    }
    let result = kept.join('\n');
    result = result.replace(/\{\{=\s*([\s\S]+?)\s*\}\}/g, (_, content) => {
        const isOpt = content.endsWith('?');
        const expr = isOpt ? content.slice(0, -1).trimEnd() : content;
        return evalTemplateExpression(expr, variables);
    });
    result = result.replace(/\{\{(\w+)(\?)?\}\}/g, (_, name) => {
        const v = variables[name];
        return v === undefined || v === null ? '' : v;
    });
    result = result.replace(/\n{3,}/g, '\n\n');
    return result.trim();
}
function resolveTemplateVariables(reservation, table, restaurant, invoice) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const currency = restaurant === null || restaurant === void 0 ? void 0 : restaurant.currency;
    const paidPrefix = ((_a = reservation === null || reservation === void 0 ? void 0 : reservation.price) !== null && _a !== void 0 ? _a : 0) > 0 ? 'Paid ' : '';
    const internal = table === null || table === void 0 ? void 0 : table.internalName;
    const tableName = (_b = table === null || table === void 0 ? void 0 : table.name) !== null && _b !== void 0 ? _b : '';
    const tableIdentifier = internal ? `${internal} | ${tableName}` : tableName;
    const occasion = (reservation === null || reservation === void 0 ? void 0 : reservation.occasion) ? String(reservation.occasion) : '';
    const addons = (_d = (_c = reservation === null || reservation === void 0 ? void 0 : reservation.items) === null || _c === void 0 ? void 0 : _c.filter((i) => i.type === 'addon').map((i) => {
        var _a;
        const qty = (_a = i.quantity) !== null && _a !== void 0 ? _a : 1;
        const price = (Math.round((Number(i.price) || 0) * 100) / 100).toFixed(2);
        return `${qty} x ${i.label} @ $${price}`;
    }).join(', ')) !== null && _d !== void 0 ? _d : '';
    const guestNotes = (reservation === null || reservation === void 0 ? void 0 : reservation.guestNotes) ? String(reservation.guestNotes) : '';
    const depositPatterns = ((_e = restaurant === null || restaurant === void 0 ? void 0 : restaurant.depositLabelPatterns) !== null && _e !== void 0 ? _e : [])
        .map((p) => {
        try {
            return new RegExp(p);
        }
        catch (_a) {
            return null;
        }
    })
        .filter((r) => r !== null);
    let depositPatternBlock = '';
    const matchedPatternLabels = new Set();
    if (invoice === null || invoice === void 0 ? void 0 : invoice.lineItems) {
        const allDepositCharges = invoice.lineItems
            .flatMap((lineItem) => lineItem.charges || [])
            .filter((charge) => charge.target === 'deposit');
        for (const pattern of depositPatterns) {
            const matchingDeposit = allDepositCharges.find((charge) => charge.label && pattern.test(charge.label));
            if (matchingDeposit) {
                depositPatternBlock += `${reservation.guests}x ${matchingDeposit.label}\nPaid :credit_card:\n\n`;
                matchedPatternLabels.add(matchingDeposit.label);
            }
        }
    }
    depositPatternBlock = depositPatternBlock.trimEnd();
    const depositLineItems = [];
    if (invoice === null || invoice === void 0 ? void 0 : invoice.lineItems) {
        invoice.lineItems.forEach((lineItem) => {
            var _a;
            const depositCharges = ((_a = lineItem.charges) === null || _a === void 0 ? void 0 : _a.filter((charge) => charge.target === 'deposit')) || [];
            depositCharges.forEach((charge) => {
                var _a, _b;
                const amount = (_b = (_a = charge.absAmount) !== null && _a !== void 0 ? _a : charge.amount) !== null && _b !== void 0 ? _b : 0;
                if (amount > 0 && !matchedPatternLabels.has(charge.label || '')) {
                    depositLineItems.push({
                        label: charge.label || 'Deposit',
                        amount
                    });
                }
            });
        });
    }
    let depositBlock = '';
    for (const deposit of depositLineItems) {
        depositBlock += `${deposit.label}: ${formatCurrency(deposit.amount, currency)}\n`;
    }
    depositBlock = depositBlock.trimEnd();
    let depositMessage = '';
    if ((invoice === null || invoice === void 0 ? void 0 : invoice.lineItems) && depositLineItems.length > 0) {
        depositMessage = 'Payment collected, This amount must be removed from the final bill';
    }
    else if (!(invoice === null || invoice === void 0 ? void 0 : invoice.lineItems)) {
        const deposit = reservation.deposit || reservation.depositMinimumPrice;
        if (deposit && deposit > 0) {
            depositBlock = `Deposit: ${formatCurrency(deposit, currency)}`;
            depositMessage = 'Payment collected, This amount must be removed from the final bill';
        }
    }
    let depositTotalAmount = 0;
    if (invoice === null || invoice === void 0 ? void 0 : invoice.lineItems) {
        invoice.lineItems.forEach((lineItem) => {
            (lineItem.charges || []).forEach((charge) => {
                var _a, _b;
                if (charge.target === 'deposit') {
                    const amount = (_b = (_a = charge.absAmount) !== null && _a !== void 0 ? _a : charge.amount) !== null && _b !== void 0 ? _b : 0;
                    if (amount > 0) {
                        depositTotalAmount += amount;
                    }
                }
            });
        });
    }
    else {
        const deposit = reservation.deposit || reservation.depositMinimumPrice;
        if (deposit && deposit > 0) {
            depositTotalAmount = deposit;
        }
    }
    const guests = (_f = reservation === null || reservation === void 0 ? void 0 : reservation.guests) !== null && _f !== void 0 ? _f : 0;
    const depositTotal = depositTotalAmount > 0 ? formatCurrency(depositTotalAmount, currency) : '';
    const depositPerGuest = depositTotalAmount > 0 && guests > 0
        ? formatCurrency(depositTotalAmount / guests, currency)
        : '';
    const price = (_g = reservation === null || reservation === void 0 ? void 0 : reservation.price) !== null && _g !== void 0 ? _g : 0;
    const tableFee = (restaurant === null || restaurant === void 0 ? void 0 : restaurant.includeTablePriceInCopy) && price > 0
        ? formatCurrency(price, currency)
        : '';
    const tableFeeLine = (restaurant === null || restaurant === void 0 ? void 0 : restaurant.includeTablePriceInCopy) && price > 0
        ? `Table Selection Fee Paid: ${formatCurrency(price, currency)}`
        : '';
    const tableFeePerGuest = (restaurant === null || restaurant === void 0 ? void 0 : restaurant.includeTablePriceInCopy) && price > 0 && guests > 0
        ? formatCurrency(price / guests, currency)
        : '';
    const tax = (invoice === null || invoice === void 0 ? void 0 : invoice.taxTotal) !== undefined && invoice.taxTotal !== null
        ? formatCurrency(invoice.taxTotal, currency)
        : '';
    const total = (invoice === null || invoice === void 0 ? void 0 : invoice.total) !== undefined && invoice.total !== null
        ? formatCurrency(invoice.total, currency)
        : '';
    const firstName = (_h = reservation === null || reservation === void 0 ? void 0 : reservation.firstName) !== null && _h !== void 0 ? _h : '';
    const lastName = (_j = reservation === null || reservation === void 0 ? void 0 : reservation.lastName) !== null && _j !== void 0 ? _j : '';
    const fromParts = [firstName, lastName].filter((p) => String(p).trim()).join(' ');
    const fullNameRaw = reservation === null || reservation === void 0 ? void 0 : reservation.fullName;
    const fullName = fullNameRaw != null && String(fullNameRaw).trim() !== ''
        ? String(fullNameRaw).trim()
        : fromParts;
    return {
        paidPrefix,
        tableIdentifier,
        tableName,
        tableInternalName: internal ? String(internal) : '',
        firstName,
        lastName,
        fullName,
        tablzReservationId: (_k = reservation === null || reservation === void 0 ? void 0 : reservation.id) !== null && _k !== void 0 ? _k : '',
        guests: String(guests),
        guestNotes,
        occasion,
        addons,
        depositPatternBlock,
        depositBlock,
        depositMessage,
        depositTotal,
        depositPerGuest,
        tableFee,
        tableFeePerGuest,
        tableFeeLine,
        tax,
        total
    };
}
/**
 * Selects the format string to use for the RMS copy given a set of per-restaurant configs.
 *
 * Priority order:
 *  1. Configs with a non-empty condition are evaluated first, ordered by `weight` descending.
 *     The first config whose condition passes via `policyConditionApplies` wins.
 *  2. Configs with an empty/absent condition (catch-alls) are considered only after all
 *     conditional configs have been checked, ordered by `weight` descending.
 *     The highest-weight catch-all is used if no conditional config matched.
 *  3. Returns `null` if no configs are provided or none match — caller should fall back
 *     to `DEFAULT_RMS_FORMAT_STRING`.
 *
 * Weight controls priority within each group but never causes a catch-all to shadow a
 * specific condition, regardless of its weight value.
 */
function selectRmsFormat(configs, reservation, invoice) {
    if (!configs || configs.length === 0) {
        return null;
    }
    const hasCond = (c) => c.condition && !(0, isEmpty_1.default)(c.condition);
    const sortedSpecific = configs
        .filter(hasCond)
        .sort((a, b) => b.weight - a.weight);
    const sortedCatchAll = configs
        .filter((c) => !hasCond(c))
        .sort((a, b) => b.weight - a.weight);
    for (const cfg of sortedSpecific) {
        if ((0, invoice_1.policyConditionApplies)(cfg.condition, { reservation, invoice })) {
            return cfg.formatString;
        }
    }
    if (sortedCatchAll.length > 0) {
        return sortedCatchAll[0].formatString;
    }
    return null;
}
function getFormattedRmsCopy(configs, reservation, table, restaurant, invoice) {
    const picked = selectRmsFormat(configs, reservation, invoice);
    const formatString = picked !== null && picked !== void 0 ? picked : exports.DEFAULT_RMS_FORMAT_STRING;
    const vars = resolveTemplateVariables(reservation, table, restaurant, invoice);
    return interpolateFormatString(formatString, vars);
}
/** @deprecated Prefer {@link getFormattedRmsCopy} with `RmsFormatConfigs` for per-restaurant templates. */
function formatReservationDetails(reservation, table, restaurant, invoice) {
    var _a, _b;
    const guestNotes = reservation.guestNotes;
    let result = `${((_a = reservation.price) !== null && _a !== void 0 ? _a : 0) > 0 ? 'Paid ' : ''}TABLZ Reservation - Please do not move: ${(table === null || table === void 0 ? void 0 : table.internalName) ? (table === null || table === void 0 ? void 0 : table.internalName) + ' | ' : ''}${table === null || table === void 0 ? void 0 : table.name}\n\n`;
    if (reservation.occasion) {
        result += `Occasion: ${reservation.occasion}\n\n`;
    }
    const addons = (_b = reservation === null || reservation === void 0 ? void 0 : reservation.items) === null || _b === void 0 ? void 0 : _b.filter(i => i.type === 'addon').map(i => `${i.quantity} x ${i.label} @ $${i.price}`).join(', ');
    if (!!addons) {
        result += `Items: ${addons}, payment due at restaurant.\n\n`;
    }
    if (guestNotes) {
        result += `Guest Notes: ${guestNotes}\n\n`;
    }
    if (invoice === null || invoice === void 0 ? void 0 : invoice.lineItems) {
        const allDepositCharges = invoice.lineItems
            .flatMap(lineItem => lineItem.charges || [])
            .filter(charge => charge.target === 'deposit');
        const matchedPatternLabels = new Set();
        const depositLineItems = [];
        invoice.lineItems.forEach(lineItem => {
            var _a;
            const depositCharges = ((_a = lineItem.charges) === null || _a === void 0 ? void 0 : _a.filter(charge => charge.target === 'deposit')) || [];
            depositCharges.forEach(charge => {
                var _a, _b;
                const amount = (_b = (_a = charge.absAmount) !== null && _a !== void 0 ? _a : charge.amount) !== null && _b !== void 0 ? _b : 0;
                if (amount > 0 && !matchedPatternLabels.has(charge.label || '')) {
                    depositLineItems.push({
                        label: charge.label || 'Deposit',
                        amount
                    });
                }
            });
        });
        if (depositLineItems.length > 0) {
            depositLineItems.forEach(deposit => {
                result += `${deposit.label}: $${deposit.amount}\n`;
            });
            result += 'Payment collected, This amount must be removed from the final bill\n';
        }
    }
    else {
        const deposit = reservation.deposit || reservation.depositMinimumPrice;
        if (deposit && deposit > 0) {
            result += `Deposit: $${deposit}\n`;
            result += 'Payment collected, This amount must be removed from the final bill\n';
        }
    }
    if (restaurant === null || restaurant === void 0 ? void 0 : restaurant.includeTablePriceInCopy) {
        result += `Table Selection Fee Paid: ${reservation.price}\n`;
    }
    return result.trim();
}
//# sourceMappingURL=rmsInput.js.map